import React, { useCallback, useMemo, useState } from 'react';
import { TextInput, TextInputProps, useTranslate } from 'react-admin';
import { 
  parsePhoneNumber, 
  getCountries, 
  getCountryCallingCode,
  isValidPhoneNumber,
  type Country
} from 'react-phone-number-input';
import { InputAdornment, Menu, MenuItem, Box } from '@mui/material';

interface PhoneInputWithCountryProps extends Omit<TextInputProps, 'format' | 'parse'> {
  /**
   * Default country code to use (e.g., 'BR', 'US')
   * If not provided, will try to detect from the phone number
   */
  defaultCountry?: Country;
  /**
   * Custom error message for invalid phone numbers
   */
  errorText?: string;
}

/**
 * PhoneInputWithCountry component for React Admin with country flag selector.
 * Uses TextInput from React Admin to maintain consistent styling.
 * Validates and formats phone numbers to E.164 format.
 * 
 * @example
 * <PhoneInputWithCountry source="phone" label="Phone Number" defaultCountry="BR" />
 * 
 * @example
 * <PhoneInputWithCountry 
 *   source="phone" 
 *   label="Phone Number" 
 *   defaultCountry="BR"
 *   validate={[required()]} 
 * />
 */
export const PhoneInputWithCountry: React.FC<PhoneInputWithCountryProps> = ({
  source,
  defaultCountry = 'BR',
  errorText,
  validate = [],
  ...rest
}) => {
  const translate = useTranslate();
  const defaultErrorText = errorText || translate('validation.phone.invalid');

  if (!source) {
    throw new Error('PhoneInputWithCountry: Missing mandatory prop: source');
  }

  // Get current country from phone number or use default
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(defaultCountry);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Get country flag emoji using Unicode regional indicator symbols
  const getCountryFlag = useCallback((country: Country | undefined) => {
    if (!country) return '🌐';
    try {
      // Convert country code (e.g., 'BR') to flag emoji
      // Unicode regional indicator symbols: A=U+1F1E6, B=U+1F1E7, etc.
      const codePoints = country
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    } catch {
      return '🌐';
    }
  }, []);

  // Get all countries for the dropdown
  const countries = useMemo(() => getCountries(), []);

  // Phone validation function
  const validatePhone = useMemo(() => {
    return (value: string | undefined) => {
      if (!value || value.trim() === '') {
        return undefined; // Let required() handle empty values
      }

      try {
        // Try to parse and validate the phone number
        if (isValidPhoneNumber(value)) {
          return undefined;
        }
        return defaultErrorText;
      } catch (error) {
        return defaultErrorText;
      }
    };
  }, [defaultErrorText]);

  // Filter function: removes non-phone characters (letters, etc.)
  const filterPhoneInput = useCallback((value: string): string => {
    // Allow: numbers, +, spaces, parentheses, hyphens, dots
    // Remove: letters and other special characters
    return value.replace(/[^\d+\s()\-.]/g, '');
  }, []);

  // Format function: converts E.164 format to display format
  const formatValue = useMemo(() => {
    return (value: string | undefined): string => {
      if (!value || value.trim() === '') {
        return '';
      }

      try {
        // If already in E.164 format (starts with +), parse and format for display
        if (value.startsWith('+')) {
          const phoneNumber = parsePhoneNumber(value);
          if (phoneNumber) {
            // Return national format for display (e.g., "(11) 98765-4321" for Brazil)
            return phoneNumber.formatNational();
          }
        }

        // If not in E.164, try to parse it
        const phoneNumber = parsePhoneNumber(value);
        if (phoneNumber) {
          return phoneNumber.formatNational();
        }

        // If parsing fails, return as is (but filtered)
        return filterPhoneInput(value);
      } catch (error) {
        return filterPhoneInput(value);
      }
    };
  }, [filterPhoneInput]);

  // Parse function: converts display format to E.164 format for storage
  const parseValue = useMemo(() => {
    return (value: string | undefined): string => {
      if (!value || value.trim() === '') {
        return '';
      }

      // First filter out invalid characters
      const filteredValue = filterPhoneInput(value);

      try {
        // Try to parse with current selected country or default
        const countryToUse = selectedCountry || defaultCountry;
        const phoneNumber = parsePhoneNumber(filteredValue, countryToUse);
        
        if (phoneNumber && phoneNumber.isValid()) {
          // Update selected country if detected
          if (phoneNumber.country) {
            setSelectedCountry(phoneNumber.country);
          }
          // Return in E.164 format (e.g., "+5511987654321")
          return phoneNumber.number;
        }

        // If invalid, return filtered value (validation will catch it)
        return filteredValue;
      } catch (error) {
        return filteredValue;
      }
    };
  }, [selectedCountry, defaultCountry, filterPhoneInput]);

  // Handle country selection
  const handleCountrySelect = useCallback(
    (country: Country) => {
      setSelectedCountry(country);
      setAnchorEl(null);
    },
    []
  );

  const handleCountryClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Detect country from current value
  const currentCountry = useMemo(() => {
    // This will be updated when value changes via parse function
    return selectedCountry || defaultCountry;
  }, [selectedCountry, defaultCountry]);

  // Country selector component
  const CountrySelector = (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleCountryClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          padding: '4px 8px',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        <span style={{ fontSize: '1.5em' }}>{getCountryFlag(currentCountry)}</span>
        <span style={{ fontSize: '0.75em', opacity: 0.7 }}>▼</span>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 250,
          },
        }}
      >
        {countries.map((country) => {
          const callingCode = getCountryCallingCode(country);
          const flag = getCountryFlag(country);
          return (
            <MenuItem
              key={country}
              onClick={() => handleCountrySelect(country)}
              selected={country === currentCountry}
            >
              <span style={{ marginRight: 8, fontSize: '1.2em' }}>{flag}</span>
              <span style={{ marginRight: 8, fontWeight: 'bold' }}>+{callingCode}</span>
              <span>{country}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );

  // Combine custom validation with provided validators
  const combinedValidate = useMemo(() => {
    const validators = Array.isArray(validate) ? validate : [validate];
    return [...validators, validatePhone].filter(Boolean);
  }, [validate, validatePhone]);

  return (
    <TextInput
      source={source}
      format={formatValue}
      parse={parseValue}
      validate={combinedValidate}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {CountrySelector}
          </InputAdornment>
        ),
      }}
      inputProps={{
        inputMode: 'tel',
        onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
          // Allow: numbers, +, spaces, parentheses, hyphens, dots, backspace, delete, tab, escape, enter
          const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
          ];
          
          if (allowedKeys.includes(e.key)) {
            return; // Allow control keys
          }
          
          // Allow Ctrl/Cmd + A, C, V, X (select all, copy, paste, cut)
          if (e.ctrlKey || e.metaKey) {
            if (['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
              return;
            }
          }
          
          // Check if the key is a valid phone character
          const validPhoneChars = /[\d+\s()\-.]/;
          if (!validPhoneChars.test(e.key)) {
            e.preventDefault();
          }
        },
      }}
      {...rest}
    />
  );
};
